import { useCallback, useEffect, useMemo, useState } from "react";
import { CompareRajasthan } from "../../../models/rajasthan";
import { getMonthFromDate } from "../../../extensions/date";
import {
  getGridcoCollectiveData,
  getGridcoDiscomData,
  getGridcoIntraGen,
  getGridcoIsgs,
  getGridcoRegen,
} from "../../../Rest_api/RestApi";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { SearchBox } from "../../../components/Search";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { CompareGridco } from "../../../models/gridco";
import { stringToConsistentColor } from "../../../extensions/string";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const SHORT_TERM_LEGENDS = [
  {
    key: "Collective Buy",
    label: "Collective Buy",
    color: ExchangeColors[4],
    stackId: "generation",
  },
  {
    key: "Collective Sell",
    label: "Collective Sell",
    color: "magenta",
    stackId: "sell",
  },
];

const LEGENDS = [
  {
    key: "ISGS",
    label: "Interstate Generation",
    color: ExchangeColors[3],
    stackId: "generation",
  },

  {
    key: "Intra Generation",
    label: "Intrastate Generation",
    color: ExchangeColors[0],
    stackId: "generation",
  },
  {
    key: "IntraState RE Generation",
    label: "IntraState RE Generation",
    color: "#00FF00",
    stackId: "generation",
  },
];

const DISCOM_LEGENDS = [
  // AVVNL Discom, JVVNL Discom, JDVVNL Discom
  {
    key: "TPNODL Discom",
    label: "TPNODL Discom",
    stackId: "discom",

    color: ExchangeColors[1],
  },
  {
    key: "TPCODL Discom",
    label: "TPCODL Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
  {
    key: "TPWODL Discom",
    label: "TPWODL Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
  {
    key: "TPSODL Discom",
    label: "TPSODL Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
];

const TOTAL_DISCOM_LEGENDS = [
  {
    key: "Total Discom",
    label: "Discom Load",
    color: ExchangeColors[1],
    stackId: "discom",
  },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh", "LU"];
const CONSUMPTION = [
  {
    label: "Total",
    value: TOTAL_DISCOM_LEGENDS,
  },
  {
    label: "By Discom",
    value: DISCOM_LEGENDS,
  },
];

export default function GridcoOverview() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [data, setData] = useState<CompareRajasthan[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [uniqueEntities, setUniqueEntities] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedConsumption, setSelectedConsumption] = useState(
    CONSUMPTION[0],
  );
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[1]);

  const getActivity = useCallback(() => {
    return [
      { label: "Consumption", value: selectedConsumption.value },
      { label: "Generation", value: LEGENDS },
      {
        label: "Buy",
        value: SHORT_TERM_LEGENDS.filter((item) =>
          item.stackId.includes("generation"),
        ),
      },
      {
        label: "Sell",
        value: SHORT_TERM_LEGENDS.filter((item) =>
          item.stackId.includes("sell"),
        ),
      },
    ];
  }, [selectedConsumption.value]);
  const [selectedActivity, setSelectedActivity] = useState(getActivity());

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    // Initiate all API calls concurrently
    const [isgsData, collectiveData, generationData, discomData, reGenData] =
      await Promise.all([
        getGridcoIsgs({ startDate: startDateStr, endDate: endDateStr }),
        getGridcoCollectiveData({
          startDate: startDateStr,
          endDate: endDateStr,
        }),

        getGridcoIntraGen({ startDate: startDateStr, endDate: endDateStr }),
        getGridcoDiscomData(startDateStr, endDateStr),
        getGridcoRegen({ startDate: startDateStr, endDate: endDateStr }),
      ]);

    const processedIsgsData = isgsData.map((item) => ({
      ...item,
      value_mw: item.expp_value || 0,
      value_mwh: item.expp_value / 4 || 0,
      value_lu: item.expp_value / 400 || 0,
      entity: item.beneficiery_from + " " + "ISGS Station",
      type: "ISGS",
      time_block: item.slot,
      month: getMonthFromDate(item.date),
    }));
    // .filter((item) => item.value_mw > 0);

    const processedCollectiveData = collectiveData.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: isBuy ? "Collective Buy" : "Collective Sell",
        time_block: item.time_slot,
        entity: item.exchange__name + " " + item.product__name,
        value_mw: Math.abs(item.value_mw) || 0,
        value_mwh: Math.abs(item.value_mw) / 4 || 0,
        value_lu: Math.abs(item.value_mw) / 400 || 0,
        month: getMonthFromDate(item.date),
      };
    });

    const processedGenerationData = generationData.map((item) => ({
      ...item,
      type: "Intra Generation",
      value_mw: item.expp_value || 0,
      value_mwh: item.expp_value / 4 || 0,
      value_lu: item.expp_value / 400 || 0,
      entity: item.station_name + " " + "Intra Generator",
      time_block: item.slot,
      month: getMonthFromDate(item.date),
    }));

    const processedReData = reGenData.map((item) => ({
      ...item,
      type: "IntraState RE Generation",
      value_mw: item.value_mw || 0,
      value_mwh: item.value_mw / 4 || 0,
      value_lu: item.value_mw / 400 || 0,
      entity: item.generator_type__name + " RE",
      month: getMonthFromDate(item.date),
      time_block: item.slot,
    }));

    // Combine all processed data
    const newData: CompareGridco[] = [
      ...processedIsgsData,
      ...processedCollectiveData,
      ...processedGenerationData,
      ...processedReData,
    ];
    // unique entities are unique entity and toEntity
    const uniqueEntities = Array.from(
      new Set(newData.map((item) => [item.entity, item.toEntity]).flat()),
    ).filter((item) => item !== undefined) as string[];
    setUniqueEntities(uniqueEntities);

    setUniqueBlock(
      Array.from(
        new Set(newData.map((item: { time_block: any }) => item.time_block)),
      ).sort((a, b) => a - b),
    );
    setDiscomData(discomData);
    setLoading(false);
    setData(newData);
  }, [activeTab, DefaultDates]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    setSelectedActivity(getActivity());
  }, [getActivity, selectedConsumption]);

  useEffect(() => {
    const processedDiscomData = discomData.map((item) => ({
      ...item,
      type:
        (selectedConsumption.label === "Total" ? "Total" : item.discom) +
        " Discom",
      value_mwh: item.drawal_mw / 4 || 0,
      value_mw: item.drawal_mw || 0,
      value_lu: item.drawal_mw / 400 || 0,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    let filteredData = data.concat(processedDiscomData);

   

    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_block),
      );
    }
    const params: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      groupBy: ["type"],
      tableKeys: ["date", "time_block"],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mw", "value_mwh", "value_lu"],
      priceKey: "",
      averageDuplicationKeys: ["time_block", "date"],
      averageKeys: ["value_mw"],
      reverseSort: ["date", "time_block"],
      data: filteredData,
    };

    const chartData = FormatChartData(params);

    let finalData = chartData.data.map((item) => {
      // Check for valid data structure first
      if (!item) return { shortTerm: 0 };

      // Calculate total with safety checks
      const total = selectedConsumption.value.reduce((acc, column) => {
        const value = item[column.key + "_value_" + selectedUnit.toLowerCase()];
        return acc + (isNaN(value) ? 0 : Number(value) || 0);
      }, 0);

      // Calculate short term with safety checks
      const shortTerm = SHORT_TERM_LEGENDS.filter(
        (e) => e.key === "Collective Buy" || e.key === "OA_TGNA Buy",
      )
        .map((legend) => legend.key + "_value_" + selectedUnit.toLowerCase())
        .reduce((acc, column) => {
          const value = item[column];
          return acc + (isNaN(value) ? 0 : Number(value) || 0);
        }, 0);

      // Calculate percentage with extra safety
      let percent = 0;
      if (total > 0 && !isNaN(shortTerm) && !isNaN(total)) {
        percent = (shortTerm / total) * 100;
      }

      // Debug logging to identify problematic data points
      if (isNaN(percent)) {
        console.log("NaN detected:", {
          date: item.date,
          timeBlock: item.time_block,
          shortTerm,
          total,
        });
        percent = 0; // Fallback to 0 if still NaN
      }

      const dataItems = data.filter((dataItem) => {
        return (
          dataItem.type == "Collective Buy" ||
          dataItem.type == "Collective Sell"
        );
      });

      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM",
      ) as any;
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM",
      ) as any;
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" &&
          dataItem.product__name === "GDAM",
      ) as any;
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" &&
          dataItem.product__name === "RTM",
      ) as any;

      return {
        ...item,
        shortTerm: Number(percent.toFixed(2)) > 0 ? percent.toFixed(2) : 0,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });

    let finalTableData = chartData.tableData?.map((item) => {
      // Check for valid data structure first
      if (!item) return { shortTerm: 0 };

      // Calculate total with safety checks
      const total = selectedConsumption.value.reduce((acc, column) => {
        const value = item[column.key + "_value_" + selectedUnit.toLowerCase()];
        return acc + (isNaN(value) ? 0 : Number(value) || 0);
      }, 0);

      // Calculate short term with safety checks
      const shortTerm = SHORT_TERM_LEGENDS.filter(
        (e) => e.key === "Collective Buy" || e.key === "OA_TGNA Buy",
      )
        .map((legend) => legend.key + "_value_" + selectedUnit.toLowerCase())
        .reduce((acc, column) => {
          const value = item[column];
          return acc + (isNaN(value) ? 0 : Number(value) || 0);
        }, 0);

      // Calculate percentage with extra safety
      let percent = 0;
      if (total > 0 && !isNaN(shortTerm) && !isNaN(total)) {
        percent = (shortTerm / total) * 100;
      }

      // Debug logging to identify problematic data points
      if (isNaN(percent)) {
        console.log("NaN detected:", {
          date: item.date,
          timeBlock: item.time_block,
          shortTerm,
          total,
        });
        percent = 0; // Fallback to 0 if still NaN
      }

      const dataItems = data.filter((dataItem) => {
        return (
          dataItem.type == "Collective Buy" ||
          dataItem.type == "Collective Sell"
        );
      });

      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM",
      ) as any;
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM",
      ) as any;
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" &&
          dataItem.product__name === "GDAM",
      ) as any;
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" &&
          dataItem.product__name === "RTM",
      ) as any;

      return {
        ...item,
        shortTerm: Number(percent.toFixed(2)) > 0 ? percent.toFixed(2) : 0,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });
    setTableData(finalTableData || []);
    setChartData(finalData);
    setChartKeys(chartData.keys);
  }, [
    data,
    selectedKey,
    selectedConsumption,
    selectedEntity,
    selectedBlock,
    discomData,
    selectedUnit,
  ]);

  const handleUnitChange = (index: number) => {
    setSelectedUnit(UNITS[index]);
  };
  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value), // Simplified label mapping
  );

  const legends = useMemo(() => {
    const activityLegends =
      selectedActivity.length === 0
        ? selectedConsumption.value.concat(LEGENDS).concat(SHORT_TERM_LEGENDS)
        : selectedActivity.map((item) => item.value).flat();

    return activityLegends.map((legend) => ({
      dataKey: legend.key + "_value_" + selectedUnit.toLowerCase(),
      name: legend.label,
      stackId: selectedChartType.stackId ? legend.stackId : undefined,
      type: selectedChartType.value,
    }));
  }, [selectedActivity, selectedConsumption, selectedUnit, selectedChartType]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Overview By Summary",
  };
  return (
    <div className="page-container">
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={KEYS.map((item) => item.name)}
              selectedItems={[selectedKey.name]}
              onSelect={(item, index) => {
                handleKeyChange(index);
              }}
            />
            <FilterMenu
              title="Unit"
              items={UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                handleUnitChange(index);
              }}
            />
            <FilterMenu
              title="Load"
              items={CONSUMPTION.map((item) => item.label)}
              selectedItems={[selectedConsumption.label]}
              onSelect={(item, index) => {
                setSelectedConsumption(CONSUMPTION[index]);
              }}
            />
            <FilterMenu
              title="Activity"
              items={getActivity().map((item) => item.label)}
              selectedItems={selectedActivity.map((item) => item.label)}
              setSelectedItems={(v: string[]) => {
                setSelectedActivity(
                  getActivity().filter((item) => v.includes(item.label)) as any,
                );
              }}
            />
            <FilterMenu
              title="Chart Type"
              items={R_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) => {
                setSelectedChartType(R_CHART_TYPES[index]);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader />
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader />
            ) : (
              <>
                <div className="gas-heading">
                  <h2 className="chartHeading w-60">By Summary</h2>
                  <div className="flex gap-x-6  justify-end">
                    <SearchBox
                      options={uniqueEntities.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                      isMany={true}
                      placeholder="Search Entities"
                      selectedOptions={selectedEntity.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                      onChange={(item: any) => {
                        setSelectedEntity(item.map((item: any) => item.value));
                      }}
                    />
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                    <button
                      onClick={() => {
                        dataTab.label === "Chart" &&
                          downloadChartTableCSV(tablePar);
                        dataTab.label === "Summary" &&
                          downloadSummaryCSV(tablePar);
                        dataTab.label === "Table" &&
                          downloadChartTableCSV(tablePar);
                      }}
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    data={chartData}
                    xDataKey={selectedKey.firstKey}
                    xTick={
                      selectedKey.secondKey !== undefined
                        ? (value: any, index: any) => {
                            return renderHourTick(value, 10);
                          }
                        : undefined
                    }
                    secondXDataKey={selectedKey.secondKey}
                    unit={selectedUnit}
                    isTimeSlot={selectedKey.firstKey === "time_block"}
                    onlyTitle={true}
                    // secondYAxisLabel="Short Term Procurement %"
                    barCategoryGap={12}
                    yAxisLabel={selectedUnit}
                    showBrush={getShowBrush(chartData)}
                    brushIndex={getBrushIndex()}
                    legends={legends}
                    defaultSelectedLegends={selectedLegends}
                    onLegendSelect={handleLegendSelect}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummaryTable {...tablePar} />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <p>
                    1. SLDC: Daily CGP scheduling <br /> 2.ERLDC: Daily final
                    scheduling <br /> 3. Gridco : Vedanta IPP, OPGC Stage II,
                    State Hydro, ABT Meter readings, Exhange buy/sell .
                  </p>
                }
                text="1. All scheduling to Odisha Solar beneficiary is considered hybrid power.
              2. OPGC Stage I taken from state hydro file (IB Unit I & II).
            
              
"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </div>
  );

  function getActiveLegends() {
    const activityLegends =
      selectedActivity.length === 0
        ? selectedConsumption.value.concat(LEGENDS).concat(SHORT_TERM_LEGENDS)
        : selectedActivity.map((item) => item.value).flat();

    return activityLegends.map((legend) => {
      return {
        dataKey: legend.key + "_value_" + selectedUnit.toLowerCase(),
        name: legend.label,
        // stroke: stringToConsistentColor(legend.label),
        stackId: selectedChartType.stackId ? legend.stackId : undefined,
        type: selectedChartType.value,
      };
    });
    // .concat([
    //   {
    //     dataKey: "shortTerm",
    //     name: "Short Term Procurement %",
    //     yAxisId: "right",
    //     stroke: lightenColor(ExchangeColors[2], 20),
    //     stackId: "generation",
    //     type: ChartType.Line,
    //   } as any,
    // ]) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 7;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 7 };
    }
  }
}
